/**
 * Auth API - Operator identity and authentication
 *
 * The Stream operator is authenticated by the consumer API key loaded from
 * Secret Manager at app startup (see src-tauri/src/services/auth.rs). The API
 * key itself is the session — the gateway authenticates every request with it
 * and returns the consumer's scopes. There is no separate `/v1/auth/session`
 * endpoint to call.
 *
 * This module derives a local OperatorInfo object from the configured API key
 * so the rest of the UI (header avatar, mention attributions, etc.) has a
 * stable identity to render without requiring a backend round-trip.
 */

import { api } from '@/lib/api'
import { useGoogleAuthStore } from '@/features/auth/stores/google-auth-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import type { OperatorInfo } from './types'

/**
 * Build an OperatorInfo from the configured API key + active environment.
 *
 * The API key is opaque to the frontend, but the active environment name and
 * the key prefix are enough to construct a stable operator identity for UI
 * rendering. The actual permission check happens server-side on every request.
 */
export async function getOperatorIdentity(): Promise<OperatorInfo> {
  const credential = api.getApiKey()
  if (!credential) {
    throw new Error('API client not configured. Cannot derive operator identity.')
  }

  const env = useEnvironmentStore.getState()
  const activeConfig = env.getActiveConfig()
  const envName = activeConfig?.name ?? env.active

  // Under bearer auth (the browser portal) we know the operator's real email
  // from the Google sign-in, so use it rather than the synthetic placeholder
  // below. Deriving a suffix from an ID token would also be meaningless — it
  // changes every hour when the token is reissued.
  const signedInEmail = useGoogleAuthStore.getState().email
  if (signedInEmail) {
    return {
      id: `op_${envName}_${signedInEmail}`,
      email: signedInEmail,
      name: signedInEmail.split('@')[0] ?? signedInEmail,
      avatarUrl: null,
    }
  }

  // Use the last 8 chars of the API key as a stable, non-secret operator id.
  // The key itself is never exposed to the UI surface — only this short suffix.
  const keySuffix = credential.slice(-8)
  const id = `op_${envName}_${keySuffix}`

  // Friendly display name derived from the environment context.
  const name = envName === 'prod' ? 'Operator (Prod)' : 'Operator (Dev)'
  const email = `operator+${envName}@kodeon.local`

  return {
    id,
    email,
    name,
    avatarUrl: null,
  }
}

/**
 * Check if the operator is authenticated.
 * Returns true if we have a configured API key (the gateway will validate it
 * on the first real request).
 */
export async function checkAuth(): Promise<boolean> {
  try {
    await getOperatorIdentity()
    return true
  } catch {
    return false
  }
}
